import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useSettings } from "@/context/SettingsContext";
import { PARTICLE_SCALE, TIER, onTierChange } from "@/lib/perf";
import { IconXmark } from "@/components/icons/Icons";

/** Season / holiday look managed in Admin → Sozlamalar → Bayram dizayni. */
export const EFFECTS = [
  { value: "auto", label: "Avto (sanaga qarab)" },
  { value: "none", label: "O‘chiq" },
  { value: "newyear", label: "Yangi yil 2027" },
  { value: "christmas", label: "Rojdestvo" },
  { value: "winter", label: "Qish" },
  { value: "navruz", label: "Navro‘z" },
  { value: "spring", label: "Bahor" },
  { value: "summer", label: "Yoz" },
  { value: "autumn", label: "Kuz" },
  { value: "ramadan", label: "Ramazon" },
];

export function resolveEffect(effect, d = new Date()) {
  if (effect && effect !== "auto") return effect;
  const m = d.getMonth() + 1;
  const day = d.getDate();
  if ((m === 12 && day >= 20) || (m === 1 && day <= 10)) return day >= 24 && day <= 26 && m === 12 ? "christmas" : "newyear";
  if (m === 12 || m === 1 || m === 2) return "winter";
  if (m === 3 && day >= 15 && day <= 25) return "navruz";
  if (m >= 3 && m <= 5) return "spring";
  if (m >= 6 && m <= 8) return "summer";
  return "autumn";
}

const GLOW = {
  newyear: "rgba(255, 196, 92, .16)",
  christmas: "rgba(255, 92, 92, .14)",
  winter: "rgba(150, 200, 255, .14)",
  navruz: "rgba(120, 220, 140, .14)",
  spring: "rgba(255, 170, 210, .13)",
  summer: "rgba(255, 190, 90, .14)",
  autumn: "rgba(255, 140, 60, .13)",
  ramadan: "rgba(240, 210, 130, .13)",
};

const PALETTE = {
  snow: ["255,255,255"],
  christmas: ["255,255,255", "255,255,255", "255,90,90", "255,214,120", "120,220,150"],
  gold: ["255,214,120", "255,236,180", "255,255,255"],
  petals: ["255,190,215", "255,225,235", "255,255,255"],
  navruz: ["255,210,90", "120,220,140", "255,120,140", "255,255,255"],
  summer: ["255,200,110", "255,170,80", "255,235,170"],
  autumn: ["235,120,50", "250,170,60", "200,80,40", "240,200,90"],
};

function kindFor(effect) {
  switch (effect) {
    case "newyear":
      return { shape: "snow", colors: PALETTE.snow, extra: "sparkle" };
    case "christmas":
      return { shape: "snow", colors: PALETTE.christmas, extra: "sparkle" };
    case "winter":
      return { shape: "snow", colors: PALETTE.snow };
    case "navruz":
      return { shape: "petal", colors: PALETTE.navruz };
    case "spring":
      return { shape: "petal", colors: PALETTE.petals };
    case "summer":
      return { shape: "bokeh", colors: PALETTE.summer };
    case "autumn":
      return { shape: "leaf", colors: PALETTE.autumn };
    case "ramadan":
      return { shape: "star", colors: PALETTE.gold };
    default:
      return null;
  }
}

function spawn(W, H, kind, initial) {
  const r = Math.random;
  const base = { x: r() * W, y: initial ? r() * H : -20, c: kind.colors[(r() * kind.colors.length) | 0], rot: r() * Math.PI * 2, vr: (r() - 0.5) * 1.6, ph: r() * 6.28 };
  if (kind.shape === "snow") return { ...base, s: 0.8 + r() * 2.6, vy: 18 + r() * 42, vx: (r() - 0.5) * 14, a: 0.35 + r() * 0.55 };
  if (kind.shape === "petal" || kind.shape === "leaf") return { ...base, s: 4 + r() * (kind.shape === "leaf" ? 6 : 4), vy: 22 + r() * 34, vx: 8 + r() * 18, a: 0.55 + r() * 0.35 };
  if (kind.shape === "bokeh") return { ...base, y: initial ? r() * H : H + 20, s: 6 + r() * 22, vy: -(6 + r() * 14), vx: (r() - 0.5) * 8, a: 0.06 + r() * 0.12 };
  return { ...base, s: 0.8 + r() * 1.8, vy: 0, vx: 0, a: 0.3 + r() * 0.6 }; // star
}

/** Full-screen, pointer-transparent particle canvas. dt-based, pauses when hidden, scales with device tier. */
function ParticleCanvas({ effect, intensity }) {
  const ref = useRef(null);
  const [tier, setTier] = useState(TIER);
  useEffect(() => onTierChange(setTier), []);

  useEffect(() => {
    const kind = kindFor(effect);
    const cv = ref.current;
    if (!kind || !cv) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = cv.getContext("2d", { alpha: true });
    const dpr = Math.min(window.devicePixelRatio || 1, tier === "high" ? 1.75 : tier === "mid" ? 1.25 : 1);
    let W = 0;
    let H = 0;
    let parts = [];
    let sparks = [];
    const target = () => {
      const base = { low: 26, medium: 52, high: 90 }[intensity] || 52;
      const area = Math.min(1.6, (W * H) / (1280 * 800));
      return Math.max(6, Math.round(base * area * PARTICLE_SCALE[tier] + 6));
    };
    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      cv.width = W * dpr;
      cv.height = H * dpr;
      cv.style.width = `${W}px`;
      cv.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = target();
      while (parts.length < n) parts.push(spawn(W, H, kind, true));
      parts.length = n;
    };
    resize();
    window.addEventListener("resize", resize);

    let raf = 0;
    let last = performance.now();
    let t = 0;
    const draw = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      t += dt;
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        p.ph += dt;
        p.x += (p.vx + Math.sin(p.ph * 1.3) * (kind.shape === "snow" ? 10 : 18)) * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        if (p.y > H + 24 || p.y < -40 || p.x > W + 40 || p.x < -40) {
          parts[i] = spawn(W, H, kind, false);
          if (kind.shape === "bokeh") parts[i].y = H + 20;
          continue;
        }
        ctx.globalAlpha = kind.shape === "star" ? p.a * (0.55 + 0.45 * Math.sin(p.ph * 2.2)) : p.a;
        ctx.fillStyle = `rgb(${p.c})`;
        if (kind.shape === "snow" || kind.shape === "bokeh" || kind.shape === "star") {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.s, 0, 6.283);
          ctx.fill();
        } else {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.beginPath();
          if (kind.shape === "leaf") {
            ctx.moveTo(0, -p.s);
            ctx.quadraticCurveTo(p.s * 0.9, 0, 0, p.s);
            ctx.quadraticCurveTo(-p.s * 0.9, 0, 0, -p.s);
          } else ctx.ellipse(0, 0, p.s, p.s * 0.55, 0, 0, 6.283);
          ctx.fill();
          ctx.restore();
        }
      }
      // New Year / Christmas golden twinkles (mid+high tiers only)
      if (kind.extra === "sparkle" && tier !== "low") {
        if (Math.random() < dt * (tier === "high" ? 3 : 1.4)) sparks.push({ x: Math.random() * W, y: Math.random() * H * 0.55, life: 0, max: 0.9 + Math.random() * 0.8, s: 1.5 + Math.random() * 2.5 });
        sparks = sparks.filter((s) => (s.life += dt) < s.max);
        for (const s of sparks) {
          const k = Math.sin((s.life / s.max) * Math.PI);
          ctx.globalAlpha = 0.85 * k;
          ctx.strokeStyle = "rgb(255,220,140)";
          ctx.lineWidth = 1.2;
          const r = s.s * (1 + k * 2.2);
          ctx.beginPath();
          ctx.moveTo(s.x - r, s.y);
          ctx.lineTo(s.x + r, s.y);
          ctx.moveTo(s.x, s.y - r);
          ctx.lineTo(s.x, s.y + r);
          ctx.stroke();
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    const start = () => {
      cancelAnimationFrame(raf);
      last = performance.now();
      raf = requestAnimationFrame(draw);
    };
    const vis = () => (document.hidden ? cancelAnimationFrame(raf) : start());
    document.addEventListener("visibilitychange", vis);
    start();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", vis);
    };
  }, [effect, intensity, tier]);

  return <canvas ref={ref} aria-hidden="true" className="pointer-events-none fixed inset-0 z-[40]" data-testid="seasonal-canvas" data-effect={effect} />;
}

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = Date.parse(target || "");
  if (!end) return null;
  const diff = end - now;
  if (diff <= 0) return { done: true, since: -diff };
  const s = Math.floor(diff / 1000);
  return { done: false, d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

const pad = (n) => String(n).padStart(2, "0");

/** Floating glass pill with holiday greeting + live countdown (Home only, dismissible). */
function SeasonalBanner({ theme, effect }) {
  const cd = useCountdown(theme.countdown_enabled ? theme.countdown_target : null);
  const [hidden, setHidden] = useState(() => {
    try {
      return sessionStorage.getItem("pk_banner_hidden") === "1";
    } catch (_) {
      return false;
    }
  });
  const showCountdown = cd && (!cd.done || cd.since < 7 * 86400 * 1000);
  const year = new Date(theme.countdown_target || Date.now()).getFullYear();
  const text = theme.banner_text || (effect === "newyear" || effect === "christmas" ? (cd?.done ? `Yangi ${year}-yil muborak!` : "") : "");
  if (!theme.banner_enabled || hidden || (!text && !showCountdown)) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -14, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ type: "spring", stiffness: 380, damping: 32, delay: 0.6 }}
        className="pointer-events-none fixed inset-x-0 z-[48] flex justify-center px-4"
        style={{ top: "calc(64px + var(--safe-top))" }}
      >
        <div className="pk-glass pk-blur-keep pointer-events-auto flex max-w-[min(560px,calc(100vw-32px))] items-center gap-3 rounded-full py-1.5 pl-4 pr-1.5 lg:mt-6" data-testid="seasonal-banner">
          <div className="min-w-0 text-[13px] font-bold leading-tight text-white">
            {text && <span className="block truncate sm:inline" data-testid="seasonal-banner-text">{text}</span>}
            {showCountdown && !cd.done && (
              <span className="block truncate text-white/70 sm:ml-2 sm:inline" data-testid="seasonal-countdown">
                {theme.countdown_title || "Yangi yilgacha"}: <span className="font-extrabold tabular-nums text-white">{cd.d} kun {pad(cd.h)}:{pad(cd.m)}:{pad(cd.s)}</span>
              </span>
            )}
          </div>
          <button
            onClick={() => {
              setHidden(true);
              try {
                sessionStorage.setItem("pk_banner_hidden", "1");
              } catch (_) {}
            }}
            aria-label="Yopish"
            className="pk-press grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/10 text-white/80 hover:bg-white/20"
            data-testid="seasonal-banner-close"
          >
            <IconXmark size={14} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/** Mount once in App. Hidden on the player page so nothing sits above the video. */
export function SeasonalLayer({ previewEffect }) {
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const theme = settings?.theme;
  const effect = useMemo(() => resolveEffect(previewEffect || theme?.effect), [previewEffect, theme?.effect]);
  useEffect(() => {
    document.documentElement.dataset.season = effect;
  }, [effect]);
  if (!theme || effect === "none" || pathname.startsWith("/watch")) return null;
  return (
    <>
      {theme.hero_glow && GLOW[effect] && (
        <div aria-hidden="true" className="pointer-events-none fixed inset-x-0 top-0 z-[1] h-[38vh]" style={{ background: `radial-gradient(70% 100% at 50% 0%, ${GLOW[effect]}, transparent 70%)` }} data-testid="seasonal-glow" />
      )}
      <ParticleCanvas effect={effect} intensity={theme.intensity} />
      {pathname === "/" && <SeasonalBanner theme={theme} effect={effect} />}
    </>
  );
}
